import React, { useEffect } from 'react';
import { BottomNavigation, BottomNavigationAction, Box, Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import config from '../config';
import { useAuth } from '../AuthContext';
import { useStackGeneration } from '../contexts/StackGenerationContext';
import { HomeIcon, LocalHospitalIcon, HealingIcon, ShoppingCartIcon } from '../icons';

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = React.useState(0);
  const theme = useTheme();
  const showAffiliateStore = config.FEATURES.AFFILIATE_STORE;
  const { currentUser } = useAuth();
  // A stack that is generating (accent dot inside a pulsing halo) or finished
  // and unviewed (bare accent dot) lights a dot on Home; tapping it returns the
  // user to /create-stack — the live progress view while in progress, the
  // result once ready. Clears when consumed.
  const { isReady: stackReady, isInProgress: stackInProgress } = useStackGeneration();
  const stackActive = stackInProgress || stackReady;

  useEffect(() => {
    if (location.pathname === '/dashboard' || location.pathname === '/create-stack' || location.pathname === '/') {
      setValue(0);
    } else if (location.pathname.startsWith('/supplements')) {
      setValue(1);
    } else if (location.pathname.startsWith('/conditions')) {
      setValue(2);
    } else if (
      showAffiliateStore &&
      (location.pathname.startsWith('/products') || location.pathname.startsWith('/market'))
    ) {
      setValue(3);
    } else {
      setValue(-1);
    }
  }, [location.pathname, showAffiliateStore]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        // If a stack is generating or freshly finished, take the user to it.
        if (stackActive) {
          navigate('/create-stack');
        } else if (currentUser) {
          navigate('/dashboard');
        } else {
          navigate('/');
        }
        break;
      case 1:
        navigate('/supplements');
        break;
      case 2:
        navigate('/conditions');
        break;
      case 3:
        if (showAffiliateStore) {
          navigate('/market');
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        px: '0.75rem',
        pb: 'max(0.5rem, env(safe-area-inset-bottom))',
        pt: '0.4rem',
        bgcolor: 'transparent',
        zIndex: 1300,
      }}
    >
      <BottomNavigation
        value={value}
        onChange={handleChange}
        showLabels
        sx={{
          height: '4rem',
          borderRadius: '1.2rem',
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(0.75rem)',
          border: `0.0625rem solid ${alpha(theme.palette.primary.main, 0.12)}`,
          boxShadow: `0 -0.125rem 0.875rem ${alpha(theme.palette.primary.main, 0.14)}`,
          '& .MuiBottomNavigationAction-root': {
            minWidth: '4.25rem',
            maxWidth: 'none',
            margin: '0.22rem',
            borderRadius: '0.9rem',
            padding: '0.36rem 0.5rem 0.28rem',
            color: alpha(theme.palette.text.primary, 0.62),
            transition: 'all 0.16s ease',
            '& .MuiSvgIcon-root': {
              fontSize: '1.32rem',
              transition: 'transform 0.16s ease',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.69rem',
              fontWeight: 500,
              letterSpacing: '0.01em',
              lineHeight: 1.1,
              marginTop: '0.1rem',
              whiteSpace: 'nowrap',
            },
            '&:active': {
              transform: 'scale(0.96)',
            },
            '&:not(.Mui-selected):hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          },
          '& .MuiBottomNavigationAction-root.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiSvgIcon-root': {
              transform: 'translateY(-0.02rem)',
              fontSize: '1.4rem',
            },
            '& .MuiBottomNavigationAction-label': {
              fontWeight: 600,
              fontSize: '0.72rem',
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Home"
          icon={
            <Badge
              variant="dot"
              // Both states are the signal accent (D-8) — `success` resolves to
              // it. The amber this replaced was a risk colour used
              // decoratively, which the brief forbids outright.
              color="success"
              overlap="circular"
              invisible={!stackActive}
              sx={{
                '& .MuiBadge-badge': {
                  right: '0.15rem',
                  top: '0.15rem',
                  // The inner ring is a paper-coloured separator that lifts the
                  // dot off the icon beneath it; it is present in both states.
                  boxShadow: `0 0 0 0.125rem ${alpha(theme.palette.background.paper, 0.95)}`,
                  // Generating adds an accent halo outside that separator and
                  // pulses it outward; ready keeps the separator alone. See the
                  // note in Header.js — the halo is painted at rest as well as
                  // animated so the two states stay distinguishable when
                  // `prefers-reduced-motion` halts the pulse, and with colour
                  // ignored entirely.
                  //
                  // Keyframe name is bottom-nav-specific: keyframe names are
                  // global in CSS, and these frames differ from Header's (they
                  // carry the separator layer), so a shared name would let
                  // whichever injected last drive both badges.
                  ...(stackInProgress && {
                    boxShadow: `0 0 0 0.125rem ${alpha(theme.palette.background.paper, 0.95)}, 0 0 0 0.3125rem ${alpha(theme.palette.success.main, 0.3)}`,
                    animation: 'stackGenPulseBottomNav 1.5s ease-in-out infinite',
                    '@keyframes stackGenPulseBottomNav': {
                      '0%, 100%': {
                        boxShadow: `0 0 0 0.125rem ${alpha(theme.palette.background.paper, 0.95)}, 0 0 0 0.1875rem ${alpha(theme.palette.success.main, 0.4)}`,
                      },
                      '50%': {
                        boxShadow: `0 0 0 0.125rem ${alpha(theme.palette.background.paper, 0.95)}, 0 0 0 0.4375rem ${alpha(theme.palette.success.main, 0)}`,
                      },
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                    },
                  }),
                },
              }}
            >
              <HomeIcon />
            </Badge>
          }
        />
        <BottomNavigationAction label="Supplements" icon={<HealingIcon />} />
        <BottomNavigationAction label="Conditions" icon={<LocalHospitalIcon />} />
        {showAffiliateStore && <BottomNavigationAction label="Market" icon={<ShoppingCartIcon />} />}
      </BottomNavigation>
    </Box>
  );
}

export default BottomNav;